import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { BuildStatusBadge } from "../components/BuildStatusBadge";
import type { Build } from "../../repositories/buildRepository";
import type { Project } from "../../repositories/projectRepository";
import type { Storefront } from "../../repositories/storefrontRepository";
import type { DeployJob } from "../../repositories/deployJobRepository";

// API response type - excludes internal URLs, includes availability flags
type BuildResponse = Omit<Build, "artifactUrl" | "logUrl"> & {
  hasArtifact: boolean;
  hasLogs: boolean;
};

function formatDuration(start: Date | null, end: Date | null): string {
  if (!start) return "--:--";
  const endTime = end ? new Date(end).getTime() : Date.now();
  const startTime = new Date(start).getTime();
  const diffMs = endTime - startTime;
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return `${diffSecs}s`;
  const mins = Math.floor(diffSecs / 60);
  const secs = diffSecs % 60;
  return `${mins}m ${secs.toString().padStart(2, "0")}s`;
}

function formatDate(date: Date | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 border-l-4 border-primary pl-3 mb-5">
      <span className="material-symbols-outlined text-primary">{icon}</span>
      <h3 className="text-xl font-bold">{title}</h3>
    </div>
  );
}

function InfoRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: string }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <dt className="text-muted-foreground flex items-center gap-2">
        {icon && <span className="material-symbols-outlined text-[18px]">{icon}</span>}
        {label}
      </dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function PlatformBadge({ platform }: { platform: string }) {
  const config: Record<string, { icon: string; label: string; color: string }> = {
    windows: { icon: "window", label: "Windows", color: "bg-blue-50 text-blue-700 border-blue-200" },
    linux: { icon: "terminal", label: "Linux", color: "bg-orange-50 text-orange-700 border-orange-200" },
    macos: { icon: "desktop_mac", label: "macOS", color: "bg-gray-50 text-gray-700 border-gray-200" },
  };

  const { icon, label, color } = config[platform] || { icon: "devices", label: platform, color: "bg-gray-50 text-gray-700" };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium border ${color}`}>
      <span className="material-symbols-outlined text-[16px]">{icon}</span>
      {label}
    </span>
  );
}

function TriggerBadge({ trigger }: { trigger: string }) {
  const config: Record<string, { icon: string; label: string; color: string }> = {
    manual: { icon: "person", label: "Manual", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    github_push: { icon: "commit", label: "Push", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    github_tag: { icon: "sell", label: "Tag", color: "bg-purple-50 text-purple-700 border-purple-200" },
    addon: { icon: "extension", label: "Addon", color: "bg-amber-50 text-amber-700 border-amber-200" },
  };

  const { icon, label, color } = config[trigger] || { icon: "help", label: trigger, color: "bg-gray-50 text-gray-700" };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium border ${color}`}>
      <span className="material-symbols-outlined text-[16px]">{icon}</span>
      {label}
    </span>
  );
}

function DeployStatusBadge({ status }: { status: string }) {
  const config: Record<string, { icon: string; label: string; color: string }> = {
    pending: { icon: "hourglass_empty", label: "Pending", color: "bg-amber-50 text-amber-700 border-amber-200" },
    running: { icon: "sync", label: "Deploying", color: "bg-blue-50 text-blue-700 border-blue-200" },
    completed: { icon: "check_circle", label: "Deployed", color: "bg-green-50 text-green-700 border-green-200" },
    failed: { icon: "error", label: "Failed", color: "bg-red-50 text-red-700 border-red-200" },
  };

  const { icon, label, color } = config[status] || { icon: "help", label: status, color: "bg-gray-50 text-gray-700" };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium border ${color}`}>
      <span className={`material-symbols-outlined text-[16px] ${status === "running" ? "animate-spin" : ""}`}>{icon}</span>
      {label}
    </span>
  );
}

function getStoreName(store: string): string {
  const names: Record<string, string> = {
    steam: "Steam",
    itch_io: "itch.io",
    epic: "Epic Games",
    gog: "GOG",
  };
  return names[store] || store;
}

export function BuildDetailPage() {
  const { id, buildId } = useParams<{ id: string; buildId: string }>();
  const navigate = useNavigate();

  const [build, setBuild] = useState<BuildResponse | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [logs, setLogs] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storefronts, setStorefronts] = useState<Storefront[]>([]);
  const [deployJobs, setDeployJobs] = useState<DeployJob[]>([]);
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [selectedDeployJobId, setSelectedDeployJobId] = useState<string | null>(null);
  const [deployLogs, setDeployLogs] = useState<string | null>(null);
  const [deployLogsLoading, setDeployLogsLoading] = useState(false);

  const handleDeploy = async (storefrontId: string) => {
    if (!buildId) return;

    setDeploying(true);
    setDeployError(null);

    try {
      const response = await fetch(`/api/builds/${buildId}/deploy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ storefrontId }),
      });

      if (!response.ok) {
        const { error } = await response.json();
        throw new Error(error || "Failed to trigger deployment");
      }

      const { data: newDeployJob } = await response.json();
      setDeployJobs((prev) => [newDeployJob, ...prev]);
    } catch (err) {
      setDeployError(err instanceof Error ? err.message : "Failed to trigger deployment");
    } finally {
      setDeploying(false);
    }
  };

  const handleViewDeployLogs = async (jobId: string) => {
    if (selectedDeployJobId === jobId) {
      // Toggle off if clicking the same job
      setSelectedDeployJobId(null);
      setDeployLogs(null);
      return;
    }

    setSelectedDeployJobId(jobId);
    setDeployLogs(null);
    setDeployLogsLoading(true);

    try {
      const response = await fetch(`/api/deploy-jobs/${jobId}/logs`, {
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) {
          setDeployLogs("No logs available for this deployment.");
        } else {
          throw new Error("Failed to fetch deploy logs");
        }
        return;
      }

      const { data } = await response.json();
      setDeployLogs(data);
    } catch (err) {
      setDeployLogs(`Error loading logs: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setDeployLogsLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      if (!buildId) return;

      try {
        const buildRes = await fetch(`/api/builds/${buildId}`, {
          credentials: "include",
        });

        if (!buildRes.ok) {
          if (buildRes.status === 404) {
            setError("Build not found");
            return;
          }
          throw new Error("Failed to fetch build");
        }

        const { data: buildData } = await buildRes.json();
        setBuild(buildData);

        // Fetch project details, storefronts, and deploy jobs in parallel
        const [projectRes, storefrontsRes, deployJobsRes] = await Promise.all([
          fetch(`/api/projects/${buildData.projectId}`, { credentials: "include" }),
          fetch(`/api/projects/${buildData.projectId}/storefronts`, { credentials: "include" }),
          fetch(`/api/builds/${buildId}/deploy-jobs`, { credentials: "include" }),
        ]);

        if (projectRes.ok) {
          const { data: projectData } = await projectRes.json();
          setProject(projectData);
        }

        if (storefrontsRes.ok) {
          const { data: storefrontsData } = await storefrontsRes.json();
          setStorefronts(storefrontsData);
        }

        if (deployJobsRes.ok) {
          const { data: deployJobsData } = await deployJobsRes.json();
          setDeployJobs(deployJobsData);
        }

        // Fetch logs if available
        if (buildData.hasLogs) {
          setLogsLoading(true);
          try {
            const logsRes = await fetch(`/api/builds/${buildId}/logs`, {
              credentials: "include",
            });
            if (logsRes.ok) {
              const { data: logsData } = await logsRes.json();
              setLogs(logsData);
            }
          } catch {
            // Logs might not be available yet
          } finally {
            setLogsLoading(false);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    // Poll for updates if build is in progress
    const interval = setInterval(async () => {
      if (!buildId) return;

      try {
        const buildRes = await fetch(`/api/builds/${buildId}`, {
          credentials: "include",
        });
        if (buildRes.ok) {
          const { data: buildData } = await buildRes.json();
          setBuild(buildData);

          // Stop polling if build is complete
          if (buildData.status === "completed" || buildData.status === "failed") {
            clearInterval(interval);

            // Fetch logs if they just became available
            if (buildData.hasLogs && !logs) {
              const logsRes = await fetch(`/api/builds/${buildId}/logs`, {
                credentials: "include",
              });
              if (logsRes.ok) {
                const { data: logsData } = await logsRes.json();
                setLogs(logsData);
              }
            }
          }
        }
      } catch {
        // Ignore polling errors
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [buildId, logs]);

  // Poll for deploy job updates
  useEffect(() => {
    if (!buildId) return;

    // Check if any deploy jobs are pending or running
    const hasActiveDeployJobs = deployJobs.some(
      (job) => job.status === "pending" || job.status === "running"
    );

    if (!hasActiveDeployJobs) return;

    const interval = setInterval(async () => {
      try {
        const deployJobsRes = await fetch(`/api/builds/${buildId}/deploy-jobs`, {
          credentials: "include",
        });
        if (deployJobsRes.ok) {
          const { data: deployJobsData } = await deployJobsRes.json();
          setDeployJobs(deployJobsData);
        }
      } catch {
        // Ignore polling errors
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [buildId, deployJobs]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto w-full px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading build...</div>
        </div>
      </div>
    );
  }

  if (error || !build) {
    return (
      <div className="max-w-6xl mx-auto w-full px-6 py-8">
        <div className="text-center py-16">
          <span className="material-symbols-outlined text-6xl text-muted-foreground/30 mb-4 block">error</span>
          <h2 className="text-xl font-semibold mb-4">{error || "Build not found"}</h2>
          <Button onClick={() => navigate(-1)}>
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto w-full px-6 py-8">
      {/* Breadcrumb and Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div className="flex flex-col gap-2">
          {/* Breadcrumbs */}
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Link to="/projects" className="hover:text-primary transition-colors flex items-center gap-1">
              <span className="material-symbols-outlined text-[18px]">folder_open</span>
              Projects
            </Link>
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
            <Link to={`/projects/${build.projectId}`} className="hover:text-primary transition-colors">
              {project?.name || "Project"}
            </Link>
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
            <span className="font-medium text-foreground">Build #{build.buildNumber}</span>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">Build #{build.buildNumber}</h1>
            <BuildStatusBadge status={build.status} />
          </div>
          <div className="flex items-center gap-3 text-muted-foreground">
            {build.gitRef && (
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-[16px]">call_split</span>
                {build.gitRef}
              </span>
            )}
            {build.gitCommit && (
              <span className="flex items-center gap-1 font-mono text-sm">
                <span className="material-symbols-outlined text-[16px]">commit</span>
                {build.gitCommit.substring(0, 7)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/projects/${build.projectId}`)}
            className="shadow-sm"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            Back to Project
          </Button>
          {build.hasArtifact && (
            <Button asChild className="shadow-lg shadow-primary/20">
              <a href={`/api/builds/${build.id}/artifact`}>
                <span className="material-symbols-outlined text-[20px]">download</span>
                Download Artifact
              </a>
            </Button>
          )}
        </div>
      </div>

      {build.errorMessage && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg flex items-start gap-3">
          <span className="material-symbols-outlined">error</span>
          <div>
            <strong className="block mb-1">Build Error</strong>
            {build.errorMessage}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Build Information */}
        <section>
          <SectionHeader icon="info" title="Build Information" />
          <div className="p-6 rounded-xl bg-card border border-border shadow-sm">
            <dl>
              <InfoRow label="Platform" value={<PlatformBadge platform={build.platform} />} />
              <InfoRow label="Trigger" value={<TriggerBadge trigger={build.trigger} />} />
              {build.godotVersion && (
                <InfoRow label="Godot Version" value={build.godotVersion} icon="sports_esports" />
              )}
              {build.exportPreset && (
                <InfoRow label="Export Preset" value={build.exportPreset} icon="tune" />
              )}
              <InfoRow label="Attempt" value={`${build.attempt} / ${build.maxAttempts}`} icon="repeat" />
              {build.artifactSizeBytes && (
                <InfoRow label="Artifact Size" value={formatBytes(build.artifactSizeBytes)} icon="folder_zip" />
              )}
            </dl>
          </div>
        </section>

        {/* Timing */}
        <section>
          <SectionHeader icon="schedule" title="Timing" />
          <div className="p-6 rounded-xl bg-card border border-border shadow-sm">
            <dl>
              <InfoRow label="Created" value={formatDate(build.createdAt)} icon="add_circle" />
              <InfoRow label="Started" value={formatDate(build.startedAt)} icon="play_circle" />
              <InfoRow label="Completed" value={formatDate(build.completedAt)} icon="check_circle" />
              <InfoRow
                label="Duration"
                value={
                  <span className="font-mono">
                    {formatDuration(build.startedAt, build.completedAt)}
                  </span>
                }
                icon="timer"
              />
            </dl>
          </div>
        </section>
      </div>

      {build.commitMessage && (
        <section className="mb-8">
          <SectionHeader icon="commit" title="Commit Message" />
          <div className="p-6 rounded-xl bg-card border border-border shadow-sm">
            <pre className="whitespace-pre-wrap font-mono text-sm bg-muted p-4 rounded-lg">
              {build.commitMessage}
            </pre>
          </div>
        </section>
      )}

      {/* Deployment Section - only show for completed builds with storefronts configured */}
      {build.status === "completed" && build.hasArtifact && storefronts.filter(s => s.enabled && s.store === "steam").length > 0 && (
        <section className="mb-8">
          <SectionHeader icon="cloud_upload" title="Deployment" />
          <div className="p-6 rounded-xl bg-card border border-border shadow-sm">
            {deployError && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg text-sm">
                {deployError}
              </div>
            )}

            {/* Deploy Jobs List */}
            {deployJobs.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Deployment History</h4>
                <div className="space-y-2">
                  {deployJobs.map((job) => {
                    const storefront = storefronts.find(s => s.id === job.storefrontId);
                    const isSelected = selectedDeployJobId === job.id;
                    const hasLogs = job.status === "completed" || job.status === "failed";
                    return (
                      <div key={job.id}>
                        <div
                          className={`flex items-center justify-between p-3 bg-muted/50 rounded-lg ${hasLogs ? "cursor-pointer hover:bg-muted/70" : ""} ${isSelected ? "ring-2 ring-primary" : ""}`}
                          onClick={() => hasLogs && handleViewDeployLogs(job.id)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-[20px] text-muted-foreground">
                              {storefront?.store === "steam" ? "sports_esports" : "storefront"}
                            </span>
                            <div>
                              <div className="font-medium">
                                {storefront ? getStoreName(storefront.store) : "Unknown Store"}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatDate(job.createdAt)}
                                {job.steamBranch && ` • Branch: ${job.steamBranch}`}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {job.status === "completed" && job.steamBuildId && (
                              <span className="text-xs text-muted-foreground font-mono">
                                Build #{job.steamBuildId}
                              </span>
                            )}
                            {job.errorMessage && (
                              <span className="text-xs text-destructive max-w-[200px] truncate" title={job.errorMessage}>
                                {job.errorMessage}
                              </span>
                            )}
                            {hasLogs && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDeployLogs(job.id);
                                }}
                                className="h-7 px-2"
                              >
                                <span className="material-symbols-outlined text-[16px]">
                                  {isSelected ? "expand_less" : "terminal"}
                                </span>
                              </Button>
                            )}
                            <DeployStatusBadge status={job.status} />
                          </div>
                        </div>
                        {/* Deploy Logs Display */}
                        {isSelected && (
                          <div className="mt-2 rounded-lg bg-zinc-900 text-zinc-100 overflow-hidden">
                            {deployLogsLoading ? (
                              <div className="flex items-center justify-center py-8 gap-2">
                                <span className="material-symbols-outlined animate-spin">progress_activity</span>
                                <span>Loading logs...</span>
                              </div>
                            ) : (
                              <pre className="whitespace-pre-wrap font-mono text-sm p-4 max-h-[400px] overflow-auto">
                                {deployLogs || "No logs available"}
                              </pre>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Deploy Button */}
            {(() => {
              const enabledStorefronts = storefronts.filter(s => s.enabled && s.store === "steam");
              const hasActiveDeployment = deployJobs.some(
                job => job.status === "pending" || job.status === "running"
              );

              if (enabledStorefronts.length === 0) {
                return (
                  <p className="text-sm text-muted-foreground">
                    No storefronts configured.{" "}
                    <Link to={`/projects/${build.projectId}/storefronts`} className="text-primary hover:underline">
                      Configure storefronts
                    </Link>
                  </p>
                );
              }

              if (hasActiveDeployment) {
                return (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="material-symbols-outlined text-[18px] animate-spin">sync</span>
                    Deployment in progress...
                  </p>
                );
              }

              return (
                <div className="flex flex-wrap gap-3">
                  {enabledStorefronts.map((storefront) => (
                    <Button
                      key={storefront.id}
                      onClick={() => handleDeploy(storefront.id)}
                      disabled={deploying}
                      variant="outline"
                    >
                      <span className="material-symbols-outlined text-[20px]">
                        {storefront.store === "steam" ? "sports_esports" : "cloud_upload"}
                      </span>
                      {deploying ? "Deploying..." : `Deploy to ${getStoreName(storefront.store)}`}
                    </Button>
                  ))}
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* Build Logs */}
      <section>
        <SectionHeader icon="terminal" title="Build Logs" />
        <div className="rounded-xl bg-card border border-border shadow-sm overflow-hidden">
          {logs ? (
            <pre className="whitespace-pre-wrap font-mono text-sm bg-zinc-900 text-zinc-100 p-6 max-h-[600px] overflow-auto">
              {logs}
            </pre>
          ) : (
            <div className="text-center py-16 text-muted-foreground">
              {logsLoading ? (
                <div className="flex flex-col items-center gap-2">
                  <span className="material-symbols-outlined text-4xl animate-spin">progress_activity</span>
                  <div>Loading logs...</div>
                </div>
              ) : build.status === "building" || build.status === "uploading" ? (
                <div className="flex flex-col items-center gap-2">
                  <span className="relative flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-primary" />
                  </span>
                  <div className="font-medium">Build in progress...</div>
                  <div className="text-sm">Logs will appear when the build completes</div>
                </div>
              ) : build.status === "pending" || build.status === "claimed" ? (
                <div className="flex flex-col items-center gap-2">
                  <span className="material-symbols-outlined text-4xl">hourglass_empty</span>
                  <div>Waiting for build to start...</div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <span className="material-symbols-outlined text-4xl">description</span>
                  <div>No logs available for this build</div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
